import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
} from '@angular/core';
import { BehaviorSubject, first, tap } from 'rxjs';

import { nonNullish } from '../../../lib/non-nullish';
import { ToastService } from '../../standalone-components/toast/service/toast.service';
import { VerificationService } from '../service/verification.service';
import { VerificationEventStateHolderService } from '../verification-event-state-holder/verification-event-state-holder.service';

@Component({
  templateUrl: './phone-verification.component.html',
  styleUrls: ['./phone-verification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PhoneVerificationComponent implements OnInit {
  verificationCode: string | null = null;
  isConfirmButtonDisabled$ = new BehaviorSubject<boolean>(true);
  isConfirmButtonClicked$ = this.verificationService.isPhoneCodeSent$;
  @Output() submitted = new EventEmitter<string>();

  constructor(
    private readonly toastService: ToastService,
    private readonly verificationService: VerificationService,
    private readonly verificationEventStateHolder: VerificationEventStateHolderService,
  ) {}

  ngOnInit(): void {
    this.verificationEventStateHolder
      .getVerificationEventSource()
      .subscribe((reader) => {
        if (reader) {
          this.verificationEventStateHolder.processReader(reader);
          this.submitted.emit();
        }
      });

    this.sendVerificationCodeToPhone().subscribe();
  }

  onCodeCompleted(code: string) {
    this.verificationCode = code;
  }

  onCodeChanged(code: string) {
    this.isConfirmButtonDisabled$.next(code.length !== 4);
  }

  verifyPhone() {
    this.isConfirmButtonDisabled$.next(true);
    this.verificationService
      .verifyPhoneNumber(nonNullish(this.verificationCode))
      .pipe(
        tap((res) => {
          const phoneVerificationResponse = res[0];

          this.submitted.emit();
          this.verificationEventStateHolder.disconnectVerificationStream(
            phoneVerificationResponse.userUuid,
          );
        }),
      )
      .subscribe();
  }

  sendVerificationCodeToPhone(isRepeatedCall?: boolean) {
    return this.verificationService.sendVerificationCodeToPhone().pipe(
      first(),
      tap((response) => {
        if (!isRepeatedCall) {
          this.verificationEventStateHolder.setVerificationEventSource(
            response.userUuid,
          );
        }
      }),
    );
  }

  receiveAnotherCode() {
    this.sendVerificationCodeToPhone(true)
      .pipe(
        tap(() =>
          this.toastService.show(
            $localize`You should receive SMS code on your mobile phone`,
          ),
        ),
      )
      .subscribe();
  }

  changePhoneNumber() {
    this.verificationService.displayChangePhoneNumberDialog();
  }
}
